import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';
import Book3D from './ui/Book3D';
import CoverEditorDemo from './CoverEditorDemo';
import { resolveGenreToStyleIndex } from '../lib/covers/resolve-style';

const {
  FiArrowRight, FiImage, FiLayers, FiEdit3, FiCheckCircle, FiSmartphone, FiHeadphones, FiBook, FiGrid, FiZap,
} = FiIcons;

// the same idea as the in-app Format Showcase: one cover, rendered in every format
const formats = [
  { icon: FiBook, t: 'Paperback (3D)', d: 'Standing, with spine — your Amazon listing shot.' },
  { icon: FiLayers, t: 'Full wrap', d: 'Back + spine + front, KDP geometry & bleed, print-ready.' },
  { icon: FiSmartphone, t: 'Kindle / ebook', d: '2560×1600, sRGB — exactly what the store wants.' },
  { icon: FiHeadphones, t: 'Audiobook', d: '3000×3000 square for ACX, Findaway, Spotify.' },
  { icon: FiGrid, t: 'Social', d: 'Instagram & banner crops for launch day.' },
  { icon: FiImage, t: 'Flat-lay & mockups', d: 'Lifestyle scenes for your author site.' },
];

const directions = [
  { name: 'Minimalist Literary', sw: ['#1a1a2e', '#e8c4a0', '#c9a227'], c: 70, l: 92, img: '/img/cover-examples/minimalist-texture.jpg' },
  { name: 'Bold Commercial', sw: ['#0b1d3a', '#ff5a3c', '#ffd23f'], c: 94, l: 60, img: '/img/cover-examples/dramatic-mood.jpg' },
  { name: 'Warm & Human', sw: ['#3a2a1e', '#e6b87a', '#a8623a'], c: 80, l: 78, img: '/img/cover-examples/warm-full.jpg' },
  { name: 'Atmospheric', sw: ['#12161b', '#2e4756', '#6fb3c9'], c: 76, l: 86, img: '/img/cover-examples/atmospheric.jpg' },
  { name: 'Type-Forward', sw: ['#f4f1ea', '#1a3a4a', '#d8492b'], c: 85, l: 74, img: '/img/cover-examples/noir-mood.jpg' },
];

const kdp = [
  'Spine width from your page count & paper', 'Bleed 0.125" on every edge', 'All art ≥ 300 DPI',
  'ISBN barcode zone reserved', 'RGB profile (KDP-correct)', 'Title/author contrast checked',
];

const CoverStudioPage = () => {
  const [genreQuery, setGenreQuery] = useState('');
  const [styleMatch, setStyleMatch] = useState(null);
  const runStyleMatch = () => setStyleMatch(resolveGenreToStyleIndex(genreQuery, directions));

  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'Book Cover Studio — ManuscriptSmith',
    description: 'Generate 5 cover directions, edit in plain language, and see your book in every format — paperback, Kindle, audiobook, social — with KDP-ready export.',
    url: 'https://manuscriptsmith.com/cover-studio',
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Book Cover Studio — Design a KDP-Ready Cover in Your Genre | ManuscriptSmith"
        description="Turn your manuscript into 5 distinct cover directions, refine them in plain language, and export paperback, Kindle, audiobook and social formats — KDP preflight built in."
        keywords="book cover generator, kdp cover maker, self publishing cover design, ai book cover, paperback cover template, audiobook cover"
        canonicalUrl="https://manuscriptsmith.com/cover-studio"
        structuredData={structuredData}
      />
      <main>
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-4">
            <SafeIcon icon={FiImage} className="w-4 h-4" /> Cover Studio
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            A cover that fits your book — and <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">every format it ships in.</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Cover Studio reads your manuscript, proposes five real directions for your genre, and lets you refine
            them in plain English. Then it shows your book as a 3D paperback, a Kindle, an audiobook, and social cards — all from one cover.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3">
            <Link to="/signup" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600">
              Design my cover <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/how-it-works" className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-white">How it works</Link>
          </div>
        </section>

        {/* 5 directions */}
        <section className="bg-white py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <SafeIcon icon={FiZap} className="w-8 h-8 text-primary-500 mx-auto mb-3" />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Five directions, scored for your genre</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Not one random image — five distinct concepts with palette, type and a commercial vs. literary read, so you choose with intent.</p>
            </div>

            {/* Type your own genre — matches to the closest STYLE direction below (same
                keyword-match + honest-fallback logic as the real backend's resolveGenre). */}
            <div className="max-w-xl mx-auto mb-8">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={genreQuery}
                  onChange={(e) => setGenreQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runStyleMatch()}
                  placeholder="Type your genre — e.g. &quot;cozy mystery&quot;, &quot;grimdark epic fantasy&quot;, &quot;leadership nonfiction&quot;"
                  maxLength={80}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-primary-500"
                />
                <button type="button" onClick={runStyleMatch} disabled={!genreQuery.trim()}
                  className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 whitespace-nowrap">
                  Match my genre
                </button>
              </div>
              {styleMatch && (
                <p className="text-[12px] text-gray-500 mt-1.5 text-center">
                  {styleMatch.matched
                    ? <>Closest direction: <span className="font-semibold text-primary-700">{styleMatch.name}</span> — typically used for <span className="italic">{styleMatch.reason}</span>.</>
                    : <>No exact match for "{genreQuery}" — defaulted to <span className="font-semibold text-primary-700">Minimalist Literary</span>, the platform's default direction, same as the real app.</>
                  }
                </p>
              )}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {directions.map((d, i) => (
                <div key={d.name} className={`rounded-xl border-2 overflow-hidden bg-white transition-all ${styleMatch?.index === i ? 'border-primary-500 shadow-md scale-[1.02]' : 'border-gray-200'}`}>
                  {d.img ? (
                    <div className="bg-cover bg-center" style={{ aspectRatio: '2 / 3', backgroundImage: `url(${d.img})` }} />
                  ) : (
                    <div className="flex" style={{ aspectRatio: '2 / 3' }}>{d.sw.map((c, i) => <div key={i} className="flex-1" style={{ background: c }} />)}</div>
                  )}
                  <div className="p-3">
                    <div className="font-semibold text-sm text-gray-800 flex items-center gap-1.5">{d.name} {styleMatch?.index === i && <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 text-primary-500" />}</div>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400"><span>Commercial {d.c}</span>·<span>Literary {d.l}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3D preview — full-page showcase */}
        <section className="bg-gradient-to-b from-white to-parchment-50 py-20 border-t border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Preview your covers as real 3D books</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Don't judge a cover as a flat thumbnail. See each of your five directions as an actual 3D book — a paperback, an ebook, and a launch-ready social card.</p>
            </div>
            <div className="flex flex-wrap items-end justify-center gap-10 md:gap-16 py-8">
              <Book3D coverImage="/img/cover-examples/atmospheric-full.jpg" width={220} angle={-26} />
              <Book3D coverImage={styleMatch ? directions[styleMatch.index].img : '/img/cover-examples/warm-full.jpg'} width={260} angle={-14} />
              <Book3D coverImage="/img/cover-examples/minimal-full.jpg" width={190} angle={-30} />
            </div>
            {styleMatch && (
              <p className="text-center text-[12px] text-gray-400 -mt-2 mb-6">Center book updated to your matched direction — <span className="font-semibold text-primary-600">{styleMatch.name}</span></p>
            )}
            <div className="text-center mt-10">
              <Link to="/cover-showcase" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600">
                Preview 5 covers in 3D <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Direct-edit demo — placement / typography / colors, live, same controls as the real app */}
        <section className="py-16 bg-white border-t border-parchment-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <SafeIcon icon={FiEdit3} className="w-8 h-8 text-primary-500 mx-auto mb-3" />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Change placement, type and color — directly</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Move the title, swap the typeface, pick new colors — or just ask. Try it right here, live.</p>
            </div>
            <CoverEditorDemo />
          </div>
        </section>

        {/* Format showcase */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <SafeIcon icon={FiGrid} className="w-8 h-8 text-primary-500 mx-auto mb-3" />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Your book, in every format</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">One approved cover, instantly represented everywhere you'll publish and market — no re-designing for each store.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {formats.map((f) => (
                <div key={f.t} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <SafeIcon icon={f.icon} className="w-6 h-6 text-secondary-600 mb-3" />
                  <div className="font-semibold text-gray-800">{f.t}</div>
                  <div className="text-sm text-gray-500 mt-1">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Plain-language edits */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <SafeIcon icon={FiEdit3} className="w-8 h-8 text-primary-500 mb-3" />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Refine it like you'd ask a designer</h2>
              <p className="text-gray-600 mb-4">No layers, no sliders. Type what you want — every edit is saved as a new version, so you never lose a cover you liked.</p>
              <ul className="space-y-2">
                {['Make the background darker', 'Bolder, larger title', 'Replace the figure with a lighthouse', 'More literary, less commercial'].map((s) => (
                  <li key={s} className="flex items-center gap-2 text-sm text-gray-700"><SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500" /> "{s}"</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-primary-200 bg-parchment-50 p-6">
              <div className="text-xs font-bold uppercase tracking-wide text-primary-700 mb-3">KDP preflight — built in</div>
              <ul className="space-y-2">
                {kdp.map((k) => (
                  <li key={k} className="flex items-center gap-2 text-sm text-gray-700"><SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500" /> {k}</li>
                ))}
              </ul>
              <div className="mt-4 text-xs text-gray-500">Export unlocks only when your cover passes — so it's not rejected at upload.</div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Give your book the cover it deserves</h2>
          <p className="text-gray-600 mb-6">Cover Studio is part of ManuscriptSmith — write, refine, and publish in one place.</p>
          <Link to="/signup" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600">
            Get started <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
          </Link>
        </section>
      </main>
    </div>
  );
};

export default CoverStudioPage;
